import { StyledCommentContainer } from 'components/Comments/elements';
import { TextDetail, TextDetailSmall } from 'pages/Project/elements';
import React, { useCallback, useMemo, useState } from 'react';
import type { GroupedComments, Role, Comment, ProjectStatus } from 'types';
import { ReviewerResponse } from 'types';
import { toDisplayDate } from 'utils/date';
import Collapse from '@mui/material/Collapse';
import { Flex, Comment as CommentBox } from '@cognite/cogs.js';
import { useDispatch } from 'react-redux';
import { useEventUpdate } from 'hooks/useEventUpdate';
import projectSlice from 'redux/reducers/project';
import { shouldResetReviewerResponse } from 'utils/comments';
import { AuthorAvatar, ChevronIcon, CommentBoxWrapper } from './elements';
import ActionButtonGroup from './components/ActionButtonGroup/ActionButtonGroup';
import ReplyCommentsThread from './components/ReplyCommentsThread/ReplyCommentsThread';

type CommentThreadProps = {
  groupedComment: GroupedComments;
  userRole: Role;
  user: string;
  identifier?: string;
  lastUnresolvedComment?: Comment[] | null;
  isWorkStepsResolved: boolean;
  projectStatus: ProjectStatus;
  onReplySave: (comment: Comment) => Promise<boolean>;
};

const CommentThread = ({
  groupedComment,
  userRole,
  user,
  identifier,
  lastUnresolvedComment,
  isWorkStepsResolved,
  projectStatus,
  onReplySave,
}: CommentThreadProps) => {
  const dispatch = useDispatch();
  const parentComment = groupedComment.parent;
  const hasChildComments = useMemo(
    () => !!groupedComment.children?.length,
    [groupedComment.children]
  );
  const lastUnresolvedCommentId = useMemo(
    () => lastUnresolvedComment?.[0]?.id || '',
    [lastUnresolvedComment]
  );

  const [message, setMessage] = useState('');
  const [checked, setChecked] = useState(false);

  const { updateReviewComment } = useEventUpdate();

  const handleCheck = useCallback(() => {
    setChecked((prev) => !prev);
  }, []);

  const onSaveMessage = useCallback(
    async (messageToSend?: string): Promise<void> => {
      if (messageToSend && onReplySave) {
        const createdDate = Date.now();
        await onReplySave({
          id: `comment_${identifier}_${createdDate}`,
          commentContent: messageToSend,
          role: userRole,
          author: user,
          metaData: {
            isParent: String(false),
            parentCommentId: groupedComment.parent.id,
          },
          date: createdDate,
        });
        setMessage('');

        if (shouldResetReviewerResponse(parentComment, projectStatus)) {
          const updatedParentComment: Comment = {
            ...parentComment,
            metaData: {
              ...parentComment.metaData,
              reviewerResponse: ReviewerResponse.None,
            },
          };
          await updateReviewComment(updatedParentComment);
          setChecked(true);
        }
      }
    },
    [
      onReplySave,
      identifier,
      userRole,
      user,
      groupedComment.parent.id,
      parentComment,
      projectStatus,
      updateReviewComment,
    ]
  );

  const onSubmitResponse = useCallback(async (): Promise<void> => {
    if (parentComment) {
      const updatedParentComment: Comment = {
        ...parentComment,
        metaData: {
          ...parentComment.metaData,
          isCompilerResolved: 'true',
        },
      };
      await updateReviewComment(updatedParentComment);

      if (parentComment.id === lastUnresolvedComment?.[0].id) {
        dispatch(
          projectSlice.actions.resolveComments({
            resolved: {
              name: user,
              date: Date.now(),
            },
          })
        );
      }
    }
  }, [
    parentComment,
    lastUnresolvedComment,
    dispatch,
    user,
    updateReviewComment,
  ]);

  const onSaveMessageCancel = useCallback(() => {
    setChecked((prev) => !prev);
    setMessage('');
  }, []);

  const onRejectResolution = async () => {
    if (parentComment) {
      const updatedParentComment: Comment = {
        ...parentComment,
        metaData: {
          ...parentComment.metaData,
          isCompilerResolved: 'false',
          reviewerResponse: ReviewerResponse.Rejected,
        },
      };
      await updateReviewComment(updatedParentComment);
      setChecked(true);
    }
  };

  const onConfirmResolution = useCallback(async (): Promise<void> => {
    if (parentComment) {
      const updatedParentComment: Comment = {
        ...parentComment,
        metaData: {
          ...parentComment.metaData,
          reviewerResponse: ReviewerResponse.Confirmed,
        },
      };
      await updateReviewComment(updatedParentComment);

      if (
        lastUnresolvedComment?.length &&
        lastUnresolvedComment[0]?.id &&
        parentComment.id === lastUnresolvedComment[0].id
      ) {
        dispatch(
          projectSlice.actions.resolveComments({
            resolved: {
              name: user,
              date: Date.now(),
            },
          })
        );
      }
      setChecked(false);
    }
  }, [
    parentComment,
    lastUnresolvedComment,
    dispatch,
    user,
    updateReviewComment,
    setChecked,
  ]);

  const isCompilerNotResolvedComment = useMemo(
    () => parentComment.metaData?.isCompilerResolved === 'false',
    [parentComment.metaData?.isCompilerResolved]
  );

  const shouldRenderReplyCommentBox = useMemo(
    () => checked && isCompilerNotResolvedComment,
    [checked, isCompilerNotResolvedComment]
  );

  return (
    <Flex direction="column">
      <StyledCommentContainer
        role={parentComment.role}
        aria-label={`comment-${parentComment.id}`}
      >
        <Flex direction="row" justifyContent="space-between">
          <Flex direction="column" justifyContent="space-between">
            <Flex direction="row" alignItems="center" gap={5}>
              <AuthorAvatar text={parentComment.author} />
              <TextDetailSmall>
                {toDisplayDate(parentComment.date, 'D.M.YYYY hh:mm a')}
              </TextDetailSmall>
            </Flex>
            <TextDetail wordBreak="break-word">
              {`${parentComment.commentContent} `}
            </TextDetail>
          </Flex>
          <Flex direction="column" justifyContent="space-around">
            <Flex direction="row" gap={8} alignItems="center">
              <ActionButtonGroup
                userRole={userRole}
                parentComment={parentComment}
                hasChildComments={hasChildComments}
                isWorkStepsResolved={isWorkStepsResolved}
                lastUnresolvedCommentId={lastUnresolvedCommentId}
                setChecked={setChecked}
                onSubmitResponse={onSubmitResponse}
                onRejectResolution={onRejectResolution}
                onConfirmResolution={onConfirmResolution}
              />
              {hasChildComments && (
                <ChevronIcon
                  type={checked ? 'ChevronUp' : 'ChevronDown'}
                  size={18}
                  onClick={handleCheck}
                  style={{ cursor: 'pointer' }}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
      </StyledCommentContainer>
      <Flex>
        <Collapse in={checked} style={{ width: '100%' }}>
          <ReplyCommentsThread groupedComment={groupedComment} />
        </Collapse>
      </Flex>
      {shouldRenderReplyCommentBox && (
        <CommentBoxWrapper>
          <CommentBox
            message={message}
            setMessage={setMessage}
            placeholder="Add a comment…"
            postButtonText="Save Comment"
            onPostMessage={onSaveMessage}
            onCancel={onSaveMessageCancel}
            showButtons
            cancelButtonDisabled={false}
          />
        </CommentBoxWrapper>
      )}
    </Flex>
  );
};

export default React.memo(CommentThread);
